import IconButton from "@mui/material/IconButton";

import { useAnalytics } from "@/providers";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import {
  SensorsIcon as Sensors,
  SensorsOffIcon as SensorsOff,
} from "@/shared/foundation/icons/mui";
import {
  PlotPanelConfig,
  ViewportSyncMode,
} from "@/shared/state/visualization/schema";

import { usePlotPanelContext } from "../panelContext";

import { AxisConstraintBadge } from "./AxisConstraintBadge";

const DISABLED_EXPLANATION =
  "Viewport sync is unavailable while the x axis is aligned";

/**
 * Cycles this plot's viewport sync mode; disabled while the panel's x axis
 * is aligned.
 */
export function SyncViewportButton({
  config,
}: {
  config: PlotPanelConfig | undefined;
}) {
  const { trackEvent } = useAnalytics();
  const {
    isAligned,
    viewportSyncMode: syncMode,
    setViewportSyncMode: setSyncMode,
  } = usePlotPanelContext();

  const nextSyncMode = useNextSyncMode(config);

  const onClick = () => {
    const next = nextSyncMode(syncMode);
    trackEvent("PlotPanelSyncModeChanged", { syncMode: next });
    setSyncMode(next);
  };

  // While the x axis is aligned the panel reports sync mode "" whatever the user
  // persisted (see effectiveViewportSyncMode), so a click would rewrite the stored
  // mode with nothing changing on screen, and the panel would resume in that new
  // mode once it is un-aligned.
  const button = (
    <IconButton
      aria-label="sync-chart-viewport"
      disabled={isAligned}
      onClick={onClick}
      size="small"
      title={isAligned ? undefined : "Sync viewport to all other charts"}
    >
      <AxisConstraintBadge axisConstraint={syncMode}>
        {syncMode !== "" ? (
          <Sensors color="primary" fontSize="small" />
        ) : (
          <SensorsOff color="inherit" fontSize="small" />
        )}
      </AxisConstraintBadge>
    </IconButton>
  );

  if (!isAligned) {
    return button;
  }

  // Browsers suppress the native title tooltip on a disabled control, so the
  // explanation would be missing exactly when the user needs it. A disabled
  // button also emits no pointer events for the tooltip to listen for, so the
  // span carries them instead.
  return (
    <RobotoTooltip title={DISABLED_EXPLANATION}>
      <span>{button}</span>
    </RobotoTooltip>
  );
}

function useNextSyncMode(config: PlotPanelConfig | undefined) {
  const isYLocked = !!config?.yAxis?.bounds?.locked;
  return function nextSyncMode(syncMode: ViewportSyncMode) {
    let next = nextViewportSyncMode[syncMode];
    if (isYLocked && next.includes("y")) {
      next = nextViewportSyncMode[next];
    }
    return next;
  };
}

const nextViewportSyncMode: Record<ViewportSyncMode, ViewportSyncMode> = {
  x: "xy",
  xy: "",
  "": "x",
  y: "x",
};
